<template>
  <div class="edit-form-list-item">
    <div class="history-item">
      <div class="history-item-button">
        <div class="button">
          <el-button
            :disabled="!(index > 0 && length > 1)"
            icon="el-icon-arrow-up"
            circle
            @click.stop="handleCommand('moveUp')"
          ></el-button>
        </div>
        <div class="button">
          <el-button
            :disabled="!(index + 1 != length && length > 1)"
            icon="el-icon-arrow-down"
            circle
            @click.stop="handleCommand('moveDown')"
          ></el-button>
        </div>
      </div>
      <div class="history-item-content">
        <el-form
          class="history-form"
          ref="historyItemForm"
          :model="editableItem"
          :rules="rules"
          @submit.native.prevent
        >
          <el-form-item prop="companyName" class="edit-form-item">
            <div class="item-name"><label for="companyName">会社名</label></div>
            <el-input
              class="input-lg"
              type="textbox"
              id="companyName"
              :autosize="{ minRows: 1, maxRows: 4 }"
              :placeholder="descriptionPlaceholder"
              v-model="editableItem.companyName"
              ref="desc"
            ></el-input>
          </el-form-item>

          <div class="edit-form-item">
            <div class="item-name"><label>在籍期間</label></div>
            <div class="period-start">
              <div class="form-date">
                <el-form-item label="" prop="startYear">
                  <el-input
                    class="input-xxs"
                    placeholder="2000"
                    title="年"
                    v-model="editableItem.startYear"
                    type="number"
                    :min="computedMinYear"
                    :max="computedMaxYear"
                  >
                  </el-input>
                  <span>年</span>
                </el-form-item>
                <el-form-item label="" prop="startMonth">
                  <el-input
                    class="input-xxs input-2-digit"
                    placeholder="12"
                    title="月"
                    v-model="editableItem.startMonth"
                    type="number"
                    min="1"
                    max="12"
                  >
                  </el-input>
                  <span class="unit">月から</span>
                </el-form-item>
              </div>
            </div>
            <div class="period-end">
              <div class="form-date">
                <el-form-item label="" prop="endYear">
                  <el-input
                    class="input-xxs"
                    placeholder="2000"
                    title="年"
                    v-model="editableItem.endYear"
                    type="number"
                    :min="computedMinYear"
                    :max="computedMaxYear"
                    :disabled="editableItem.isContinuing"
                  >
                  </el-input>
                  <span class="unit">年</span>
                </el-form-item>
                <el-form-item label="" prop="endMonth">
                  <el-input
                    class="input-xxs input-2-digit"
                    placeholder="12"
                    title="月"
                    v-model="editableItem.endMonth"
                    type="number"
                    min="1"
                    max="12"
                    :disabled="editableItem.isContinuing"
                  >
                  </el-input>
                  <span>月まで</span>
                  <el-checkbox
                    v-model="editableItem.isContinuing"
                    @change="changeCheckbox"
                    >在職中</el-checkbox
                  >
                </el-form-item>
              </div>
            </div>
          </div>

          <div class="projects" :class="{ project__invisible: isActive }">
            <ul
              v-for="(project, projectIndex) in item.projects"
              :key="projectIndex"
            >
              <li v-if="project.projectTitle == ''">
                プロジェクト・業務が未入力です
              </li>
              <li v-else>{{ project.projectTitle }}</li>
            </ul>
          </div>

          <div class="errors" v-if="errors">
            <ul>
              <li v-for="(error, errorIndex) in errors" :key="errorIndex">
                {{ error }}
              </li>
            </ul>
          </div>
          <div class="enter text-right"></div>
        </el-form>
      </div>
    </div>

    <ShokurekishoSkillsheetJobProjectEditPanel
      :projects="item.projects"
      :is-active="isActive"
      :min-year="computedMinYear"
      :max-year="computedMaxYear"
      @add-project="handleCommand('addProject')"
      @delete-job="handleCommand('delete')"
      @ok="deactivateHistoryItem"
      @move-up="moveUpDetail"
      @move-down="moveDownDetail"
      @delete-item="deleteDetail"
    />
  </div>
</template>

<script setup lang="ts">
import ShokurekishoSkillsheetJobProjectEditPanel from '@/components/format-edit/shokurekisho/ShokurekishoSkillsheetJobProjectEditPanel.vue'
import { useShokurekishoHistoryItem } from '@/composables/format-edit/useShokurekishoHistoryItem'
import type { ShokurekishoJobHistoryItem } from '@/composables/format-edit/useShokurekishoFormatData'

const props = defineProps<{
  item: ShokurekishoJobHistoryItem
  index: number
  length: number
  descriptionPlaceholder?: string
  descriptionMaxLength?: number
  minYear?: number | string
  maxYear?: number | string
  activeHistoryItem?: string
}>()

const emit = defineEmits<{
  (
    e: 'add-history-item',
    index: number,
    item?: ShokurekishoJobHistoryItem
  ): void
  (e: 'delete-history-item', index: number): void
  (e: 'move-history-item', params: { index: number; to: string }): void
  (
    e: 'add-project-history-item',
    index: number,
    item?: ShokurekishoJobHistoryItem
  ): void
  (e: 'delete-history-item-detail', index: number, index2: number): void
  (
    e: 'move-history-item-detail',
    params: { index: number; to: string },
    parentIndex: number
  ): void
}>()

const {
  historyItemForm,
  desc,
  errors,
  editableItem,
  isActive,
  computedMinYear,
  computedMaxYear,
  rules,
  handleCommand,
  deleteDetail,
  moveUpDetail,
  moveDownDetail,
  changeCheckbox,
  deactivateHistoryItem
} = useShokurekishoHistoryItem(props, emit)
</script>

<style lang="scss" scoped>
$white: #fff;
$errorcolor: #f56c6c;

.project__invisible {
  display: none;
}

.history-item {
  display: flex;
  flex-direction: row-reverse;
}

.history-item-content {
  flex: 1;
  margin: 0.5rem 0;
  padding: 0.5rem 1rem;

  .attention-limit {
    font-size: 0.75rem;
    color: $errorcolor;

    &.character {
      padding: 0.5rem 0;
    }

    &.column {
      background: $white;
      margin: 0.5rem 0;
      padding: 0.5rem 1rem;
    }
  }
}

.history-item-button {
  .button {
    display: flex;
  }
}

.projects {
  margin-top: 0.5rem;

  > ul {
    list-style: none;
    font-size: 0.75rem;

    li {
      &::before {
        content: '';
        display: inline-block;
        background: url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCA0OTYgNDk2Ij48Zz48cGF0aCBkPSJNMjQ4LDBDMTExLDAsMCwxMTEsMCwyNDhTMTExLDQ5NiwyNDgsNDk2LDQ5NiwzODUsNDk2LDI0OCwzODUsMCwyNDgsMFpNNDAzLjMxLDE5NS4zMWwtMTg0LDE4NGgwYTE2LDE2LDAsMCwxLTIyLjYyLDBsLTEwNC0xMDRhMTYsMTYsMCwwLDEsMC0yMi42MmwyMi42Mi0yMi42M2ExNiwxNiwwLDAsMSwyMi42MywwTDIwOCwzMDAuMTIsMzU4LjA2LDE1MC4wNmExNiwxNiwwLDAsMSwyMi42MywwbDIyLjYyLDIyLjYzQTE2LDE2LDAsMCwxLDQwMy4zMSwxOTUuMzFaIi8+PC9nPjwvc3ZnPg==');
        margin-right: 0.25rem;
        width: 0.75rem;
        height: 0.75rem;
      }
    }
  }
}
</style>
